import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Account, getExternalPlatformIdFromInternalPlatformId, getPlatformIdFromPlatform, notEmpty, OperationType, Platform, validateAndFormatAddress, } from "@orb-labs/types";
import { CreateOperationsStatus } from "@orb-labs/orby-core";
import { useOrby } from "../components/OrbyProvider/OrbyProvider.js";
import { useVirtualNode } from "../hooks/useVirtualNode.js";
import { usePlatformStorage } from "../storage/PlatformStorageContext.js";
import { useBullrunRuntime } from "../runtime/BullrunRuntimeContext.js";
const HyperliquidContext = createContext(undefined);
export const useHyperliquid = () => {
    const context = useContext(HyperliquidContext);
    if (!context) {
        throw new Error("useHyperliquid must be used within a HyperliquidProvider");
    }
    return context;
};
export const HyperliquidProvider = ({ children }) => {
    const { signTypedData: signTypedDataFromRuntime } = useBullrunRuntime();
    const storage = usePlatformStorage();
    const { accountCluster } = useOrby();
    const [isSetupComplete, setIsSetupComplete] = useState(false);
    const [isSettingUp, setIsSettingUp] = useState(false);
    const setupAttemptedFor = useRef(new Set());
    // Single-flight, keyed by wallet address. Concurrent ensureSetup callers for
    // the same address share the in-flight promise; calls for different addresses
    // (e.g. account switch mid-setup) get their own promise so a setup started
    // for A is never reused as the result for B.
    const inflightSetupByAddress = useRef(new Map());
    // Latest-known formatted address for the active account. Read inside async
    // runSetup callbacks to detect mid-setup account switches — without this
    // guard, setters like setIsSetupComplete(true) would fire for address A on
    // the provider that's now displaying address B, leaking stale state across
    // accounts.
    const currentAddressRef = useRef(undefined);
    const hyperliquidPlatformId = useMemo(() => BigInt(getPlatformIdFromPlatform(Platform.HYPERLIQUID_PLATFORM)), []);
    const arbitrumPlatformId = useMemo(() => BigInt(getPlatformIdFromPlatform(Platform.ARBITRUM)), []);
    const hyperliquidAccount = useMemo(() => Account.selectAccountForPlatform(accountCluster?.accounts ?? [], arbitrumPlatformId), [accountCluster?.accounts, arbitrumPlatformId]);
    currentAddressRef.current = validateAndFormatAddress(hyperliquidAccount?.address);
    const { virtualNode } = useVirtualNode(hyperliquidAccount?.address, hyperliquidPlatformId);
    const ensureSetup = useCallback(() => {
        const address = validateAndFormatAddress(hyperliquidAccount?.address);
        if (!address || !virtualNode)
            return Promise.resolve(false);
        if (setupAttemptedFor.current.has(address)) {
            if (currentAddressRef.current === address)
                setIsSetupComplete(true);
            return Promise.resolve(true);
        }
        // Single-flight per address: concurrent calls for the same address share
        // the in-flight promise; calls for a different address get their own.
        const existing = inflightSetupByAddress.current.get(address);
        if (existing) {
            return existing;
        }
        const runSetup = async () => {
            const commitIfStillCurrent = (fn) => {
                if (currentAddressRef.current === address)
                    fn();
            };
            try {
                const cached = await storage.hasHyperliquidSetup(address);
                if (cached) {
                    setupAttemptedFor.current.add(address);
                    commitIfStillCurrent(() => setIsSetupComplete(true));
                    return true;
                }
                commitIfStillCurrent(() => setIsSettingUp(true));
                const operationSet = await virtualNode.getOperationsToSetupOnPlatform(hyperliquidPlatformId, hyperliquidAccount);
                if (!operationSet || operationSet.status !== CreateOperationsStatus.SUCCESS) {
                    console.error("[HyperliquidProvider] getOperationsToSetupOnPlatform failed", {
                        status: operationSet?.status,
                        errorMessage: operationSet?.errorMessage,
                    });
                    return false;
                }
                const allOps = [];
                if (operationSet.intents) {
                    for (const intent of operationSet.intents) {
                        if (intent.intentOperations) {
                            allOps.push(...intent.intentOperations);
                        }
                    }
                }
                if (notEmpty(operationSet.primaryOperation)) {
                    allOps.push(operationSet.primaryOperation);
                }
                const setupOps = allOps.filter((op) => op.type === OperationType.HYPERLIQUID_SETUP && op.platformId === hyperliquidPlatformId);
                if (!setupOps.length) {
                    await storage.setHyperliquidSetup(address);
                    setupAttemptedFor.current.add(address);
                    commitIfStillCurrent(() => setIsSetupComplete(true));
                    return true;
                }
                const signedOps = [];
                for (const op of setupOps) {
                    const signature = await signTypedDataFromRuntime(op);
                    if (!signature) {
                        console.warn("[HyperliquidProvider] User declined setup signature");
                        return false;
                    }
                    signedOps.push({ signature, typedDataJson: op.data });
                }
                const externalPlatformId = getExternalPlatformIdFromInternalPlatformId(hyperliquidPlatformId);
                if (!notEmpty(externalPlatformId)) {
                    console.error("[HyperliquidProvider] Could not resolve external platform ID for Hyperliquid");
                    return false;
                }
                const result = await virtualNode.submitOperationsToSetupOnPlatform({
                    platformId: externalPlatformId,
                    address,
                    operations: signedOps,
                });
                if (!result) {
                    console.error("[HyperliquidProvider] Setup submission returned no result");
                    return false;
                }
                await storage.setHyperliquidSetup(address);
                setupAttemptedFor.current.add(address);
                commitIfStillCurrent(() => setIsSetupComplete(true));
                return true;
            }
            catch (error) {
                console.error("[HyperliquidProvider] Setup failed", error);
                return false;
            }
            finally {
                commitIfStillCurrent(() => setIsSettingUp(false));
            }
        };
        const promise = runSetup().finally(() => {
            inflightSetupByAddress.current.delete(address);
        });
        inflightSetupByAddress.current.set(address, promise);
        return promise;
    }, [signTypedDataFromRuntime, virtualNode, hyperliquidAccount, hyperliquidPlatformId, storage]);
    const reset = useCallback(async () => {
        const address = hyperliquidAccount?.address;
        if (address) {
            await storage.clearHyperliquidSetup(address);
        }
        setupAttemptedFor.current.clear();
        setIsSetupComplete(false);
    }, [hyperliquidAccount, storage]);
    const signTypedData = useCallback(async (operation) => {
        if (operation.platformId === hyperliquidPlatformId) {
            const ok = await ensureSetup();
            if (!ok)
                return undefined;
        }
        return signTypedDataFromRuntime(operation);
    }, [signTypedDataFromRuntime, ensureSetup, hyperliquidPlatformId]);
    useEffect(() => {
        const address = validateAndFormatAddress(hyperliquidAccount?.address);
        if (!address) {
            reset().catch((err) => console.error("[HyperliquidProvider] reset failed", err));
            return;
        }
        // Account changed to a new wallet: drop in-progress UI flags and restore
        // the completion flag from the per-address cache. Keeping setupAttemptedFor
        // intact lets a switch back to a previously-set-up account skip re-setup.
        setIsSettingUp(false);
        setIsSetupComplete(setupAttemptedFor.current.has(address));
    }, [hyperliquidAccount, reset]);
    const value = useMemo(() => ({
        isSetupComplete,
        isSettingUp,
        hyperliquidAccount,
        hyperliquidPlatformId,
        ensureSetup,
        signTypedData,
        reset,
    }), [isSetupComplete, isSettingUp, hyperliquidAccount, hyperliquidPlatformId, ensureSetup, signTypedData, reset]);
    return _jsx(HyperliquidContext.Provider, { value: value, children: children });
};
